import { Alert, Box, Typography, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useMaintenanceBanner, BANNER_SEVERITY } from "../../hooks/useMaintenanceBanner";
import React, { useEffect, useState } from "react";

type ActionButtonProps = {
    onClose: () => void;
};

const bannerStyles: Record<
    BANNER_SEVERITY,
    { backgroundColor: string; color: string; border: string }
> = {
    [BANNER_SEVERITY.info]: {
        backgroundColor: "#D9EDF7",
        color: "#1D1D1D",
        border: "1px solid #41717a",
    },
    [BANNER_SEVERITY.warning]: {
        backgroundColor: "#FCF8E3",
        color: "#1D1D1D",
        border: "1px solid #FAEBCC",
    },
    [BANNER_SEVERITY.error]: {
        backgroundColor: "#FDECEA",
        color: "#1D1D1D",
        border: "1px solid #F5C6CB",
    },
    [BANNER_SEVERITY.success]: {
        backgroundColor: "#E8F5E9",
        color: "#1D1D1D",
        border: "1px solid #C8E6C9",
    },
};

const ActionButton = ({ onClose }: ActionButtonProps) => {
    return (
        <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close scheduled banner"
            data-testid="close-scheduled-banner"
            sx={{ color: "inherit" }}
        >
            <Close fontSize="small" />
        </IconButton>
    );
};

const MaintenanceBanner = () => {
    const { maintenanceBanner, loading } = useMaintenanceBanner();
    const [show, setShow] = useState(true);

    useEffect(() => {
        const bannerClosed = sessionStorage.getItem("maintenanceBannerClosed");
        if (bannerClosed === "true") {
            setShow(false);
        }
    }, []);

    const handleCloseBanner = () => {
        sessionStorage.setItem("maintenanceBannerClosed", "true");
        setShow(false);
    };

    if (loading) return null;
    if (!maintenanceBanner?.isActive || !show) return null;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "start",
                alignItems: "stretch",
                gap: 1,
                padding: "24px"
            }}
            id="scheduledBanner"
            data-testid='scheduled-banner'
        >
            <Alert
                role="status"
                action={
                    maintenanceBanner.isClosable ? (
                        <ActionButton onClose={handleCloseBanner} />
                    ) : null
                }
                variant="filled"
                sx={{
                    width: "100%",
                    ...bannerStyles[maintenanceBanner.severity],
                    "& .MuiAlert-icon": {
                        color: "inherit",
                    },
                    "& .MuiAlert-action": {
                        color: "inherit",
                    },
                }}
            >
                <Typography variant="h6">{maintenanceBanner.title}</Typography>
                <Box id="scheduledBannerContent">
                    <Typography component="div">
                        {maintenanceBanner.message}{" "}
                        <Typography component="span" sx={{ fontWeight: "bold" }}>
                            Start Date:
                        </Typography>{" "}
                        {maintenanceBanner.startDate}.{" "}
                        <Typography component="span" sx={{ fontWeight: "bold" }}>
                            End Date:
                        </Typography>{" "}
                        {maintenanceBanner.endDate}
                    </Typography>
                </Box>
            </Alert>
        </Box>
    );
};

export default MaintenanceBanner;